import { io, Socket } from "socket.io-client";
import { socketLog } from "./debug";
import {
  createRealtimeEntityHandler,
  joinUserRoom,
  subscribeRealtimeChannels,
  type RealtimeEntityEvent,
} from "./events";

let socket: Socket | null = null;
let currentUserUuid: string | null = null;

// Callbacks waiting for the socket to connect
type SocketReadyCallback = (socket: Socket) => void;
const socketReadyListeners: SocketReadyCallback[] = [];

/**
 * Register a callback that fires as soon as the socket is connected.
 * If the socket is already connected, the callback is invoked immediately.
 * Returns a cancellation function to remove the callback before it fires.
 */
export const onSocketReady = (callback: SocketReadyCallback): (() => void) => {
  if (socket?.connected) {
    socketLog(
      "🔌 [onSocketReady] Socket already connected — calling callback immediately",
    );
    callback(socket);
    return () => {};
  }
  socketLog(
    "🕐 [onSocketReady] Socket not connected yet — queuing callback (pending:",
    socketReadyListeners.length + 1,
    ")",
  );
  socketReadyListeners.push(callback);
  return () => {
    const idx = socketReadyListeners.indexOf(callback);
    if (idx !== -1) socketReadyListeners.splice(idx, 1);
  };
};

type RealtimeEntityListener = (event: RealtimeEntityEvent) => void;

/**
 * Entity-event subscribers. Held here rather than on a socket so they outlive
 * it: a subscription made before the socket exists, or before a logout/login
 * swaps it for a new one, still receives events. Each socket gets a single
 * handler that fans out to this set (see `connectSocket`).
 */
const realtimeEntityListeners = new Set<RealtimeEntityListener>();

const dispatchRealtimeEntityEvent = createRealtimeEntityHandler((event) => {
  // Copied so a listener that unsubscribes mid-dispatch does not skip the next.
  Array.from(realtimeEntityListeners).forEach((listener) => listener(event));
});

/**
 * Subscribe to entity-tagged realtime pushes. Safe to call before the socket
 * has connected. Returns the unsubscribe function.
 */
export const onRealtimeEntityEvent = (
  onEvent: RealtimeEntityListener,
): (() => void) => {
  realtimeEntityListeners.add(onEvent);
  return () => {
    realtimeEntityListeners.delete(onEvent);
  };
};

export const connectSocket = (token: string, userUuid?: string): void => {
  // Covers a socket that is mid-reconnect as well as a connected one. Calling
  // `io()` again here would hand back the same cached socket and stack a second
  // copy of every handler below onto it.
  if (socket) {
    // Reconnects build a fresh transport from the manager's options, so this
    // makes them present the refreshed token instead of the expired one.
    socket.io.opts.query = { token: `Bearer ${token}` };
    if (userUuid && userUuid !== currentUserUuid) {
      currentUserUuid = userUuid;
      // Not connected yet: the `connect` handler joins with this uuid.
      if (socket.connected) {
        joinUserRoom(socket, userUuid);
        subscribeRealtimeChannels(socket, userUuid);
      }
    }
    // Server-initiated disconnects do not auto-reconnect.
    if (!socket.active) socket.connect();
    return;
  }

  const socketUrl = import.meta.env.VITE_SOCKET_URL;

  if (!socketUrl) {
    console.error("VITE_SOCKET_URL environment variable is not set");
    return;
  }

  socketLog("Connecting to socket server:", socketUrl);

  if (userUuid) {
    currentUserUuid = userUuid;
  }

  socket = io(socketUrl, {
    query: {
      token: `Bearer ${token}`,
    },
    transports: ["websocket", "polling"],
    timeout: 20000,
    reconnection: true,
    // Never stop trying. With a finite count socket.io gives up permanently
    // after a handful of failures, so any outage longer than a few seconds
    // left the app without realtime until a full page reload.
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    // A new manager each time. socket.io otherwise caches the manager per URL,
    // so after a logout/login the new socket would reuse the old one — along
    // with the previous user's token and every handler registered on it.
    forceNew: true,
  });

  socket.onAny(dispatchRealtimeEntityEvent);

  socket.on("connect", () => {
    socketLog("Socket connected successfully, socketId:", socket?.id);
    // Join user room after successful connection
    if (currentUserUuid) {
      joinUserRoom(socket!, currentUserUuid);
      subscribeRealtimeChannels(socket!, currentUserUuid);
    } else {
      console.warn(
        "⚠️ [Socket] connect fired but currentUserUuid is null —",
        "join_user_room / realtime_subscribe NOT emitted yet",
      );
    }
    // Notify any components that were waiting for the socket to be ready
    const pending = socketReadyListeners.splice(0);
    socketLog(
      "🔔 [Socket] Flushing",
      pending.length,
      "pending onSocketReady callbacks",
    );
    pending.forEach((cb) => cb(socket!));
  });

  socket.on("disconnect", (reason) => {
    socketLog("Socket disconnected:", reason);
  });

  socket.on("connect_error", (error) => {
    console.error("Socket connection error:", error);
    console.error("Error details:", {
      message: error.message,
    });

    if (error.message.includes("websocket")) {
      console.warn("WebSocket connection failed. Possible reasons:");
      console.warn("1. Socket server not running at:", socketUrl);
      console.warn("2. CORS policy blocking the connection");
      console.warn("3. Network connectivity issues");
      console.warn("4. Firewall blocking WebSocket connections");
    }
  });

  // Reconnection events live on the manager (`socket.io`) in socket.io-client
  // v4 — registering them on the socket itself never fires.
  socket.io.on("reconnect", (attemptNumber) => {
    // No re-join here: socket.io fires `connect` on every successful
    // reconnect, and that handler already re-joins and re-subscribes.
    socketLog("Socket reconnected after", attemptNumber, "attempts");
  });

  socket.io.on("reconnect_error", (error) => {
    console.error("Socket reconnection error:", error);
  });
};

export const disconnectSocket = (): void => {
  if (socket) {
    // Drop every handler before letting go, so nothing still bound to this
    // socket fires or keeps it alive after logout.
    socket.removeAllListeners();
    socket.offAny();
    socket.io.removeAllListeners();
    socket.disconnect();
    socket = null;
    currentUserUuid = null;
    socketLog("Socket manually disconnected");
  }
};

export const getSocket = (): Socket | null => socket;

export const testSocketConnection = async (): Promise<boolean> => {
  return new Promise((resolve) => {
    const testSocket = io(import.meta.env.VITE_SOCKET_URL, {
      transports: ["websocket", "polling"],
      timeout: 5000,
      forceNew: true,
    });

    const timeout = setTimeout(() => {
      testSocket.disconnect();
      socketLog("Socket connection test timed out");
      resolve(false);
    }, 10000);

    testSocket.on("connect", () => {
      socketLog("Socket connection test successful");
      clearTimeout(timeout);
      testSocket.disconnect();
      resolve(true);
    });

    testSocket.on("connect_error", (error) => {
      socketLog("Socket connection test failed:", error.message);
      clearTimeout(timeout);
      testSocket.disconnect();
      resolve(false);
    });
  });
};
